import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { MessageSquare, Trash2, CornerDownRight } from "lucide-react";
import { Input } from "@/components/ui/input";

export default function ProductQA({ slug }) {
  const { user } = useApp();
  const [qs, setQs] = useState([]);
  const [q, setQ] = useState("");
  const [guestName, setGuestName] = useState("");
  const [answering, setAnswering] = useState(null);
  const [answerText, setAnswerText] = useState("");
  const isAdmin = user?.role === "admin";

  const load = async () => setQs((await api.get(`/products/${slug}/questions`)).data);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [slug]);

  const submit = async (e) => {
    e.preventDefault();
    if (q.trim().length < 3) return;
    try {
      await api.post(`/products/${slug}/questions`, { question: q, user_name: guestName || undefined });
      setQ(""); setGuestName("");
      toast.success("Pergunta publicada");
      load();
    } catch { toast.error("Erro"); }
  };

  const sendAnswer = async (qid) => {
    if (answerText.trim().length < 1) return;
    try {
      await api.post(`/questions/${qid}/answer`, { answer: answerText });
      setAnswering(null); setAnswerText("");
      toast.success("Resposta publicada"); load();
    } catch { toast.error("Erro"); }
  };

  const remove = async (qid) => {
    if (!window.confirm("Eliminar pergunta?")) return;
    await api.delete(`/questions/${qid}`);
    load();
  };

  return (
    <section className="mt-16" data-testid="qa-section">
      <div className="flex items-center gap-2 mb-6">
        <MessageSquare className="w-5 h-5 text-primary" />
        <h2 className="text-2xl font-display font-bold">Perguntas e Respostas ({qs.length})</h2>
      </div>

      <form onSubmit={submit} className="rounded-2xl border border-border p-6 bg-card mb-6 space-y-3" data-testid="qa-form">
        {!user && (
          <Input placeholder="O teu nome (opcional)" value={guestName} onChange={(e) => setGuestName(e.target.value)} data-testid="qa-guest-name" />
        )}
        <Textarea rows={2} placeholder="A tua pergunta..." value={q} onChange={(e) => setQ(e.target.value)} data-testid="qa-question-input" />
        <Button type="submit" className="rounded-full btn-primary" data-testid="qa-submit">Publicar pergunta</Button>
      </form>

      <div className="space-y-3">
        {qs.length === 0 && <div className="text-sm text-muted-foreground text-center py-6">Ainda sem perguntas. Sê o primeiro!</div>}
        {qs.map((item) => (
          <div key={item.id} className="rounded-2xl border border-border p-5 bg-card" data-testid={`qa-item-${item.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                <div className="text-xs text-muted-foreground mb-1">{item.user_name} · {new Date(item.created_at).toLocaleDateString("pt-PT")}</div>
                <div className="font-medium text-sm">Q: {item.question}</div>
                {item.answer && (
                  <div className="mt-3 pl-4 border-l-2 border-primary/40">
                    <div className="text-[10px] uppercase tracking-widest text-primary flex items-center gap-1"><CornerDownRight className="w-3 h-3" /> VOLTIX responde</div>
                    <div className="text-sm mt-1">{item.answer}</div>
                  </div>
                )}
              </div>
              {isAdmin && (
                <div className="flex gap-1">
                  {!item.answer && <Button variant="outline" size="sm" onClick={() => { setAnswering(item.id); setAnswerText(""); }} data-testid={`qa-answer-btn-${item.id}`}>Responder</Button>}
                  <Button variant="outline" size="sm" onClick={() => remove(item.id)} data-testid={`qa-delete-${item.id}`}><Trash2 className="w-3.5 h-3.5 text-destructive" /></Button>
                </div>
              )}
            </div>
            {answering === item.id && (
              <div className="mt-3 space-y-2">
                <Textarea rows={2} value={answerText} onChange={(e) => setAnswerText(e.target.value)} placeholder="Escreve a resposta..." data-testid={`qa-answer-textarea-${item.id}`} />
                <div className="flex gap-2"><Button size="sm" className="rounded-full btn-primary" onClick={() => sendAnswer(item.id)} data-testid={`qa-answer-submit-${item.id}`}>Publicar</Button><Button size="sm" variant="outline" onClick={() => setAnswering(null)}>Cancelar</Button></div>
              </div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}
